import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, Subject } from 'rxjs';
import { MethodTypeConfigService } from '@protocols/domain';
import type { RetrieveMethodTypeOutput } from '@protocols/use-cases';

import type { MethodTypeAcquisitionField } from './method-type.state';

/**
 * One-shot result of an editing session. Payload-free on purpose: the draft
 * the consumer needs lives in this same shared state as `draft$`, so the
 * event carries only *which action* the user took. Mirrors
 * `PipelineEditorOutcome` for the pipeline editor.
 */
export type MethodTypeEditorOutcome = { kind: 'saved' } | { kind: 'cancelled' };

/**
 * Persistable projection of the editing session. Assigned pipelines and
 * resources live in their shared staging ledgers (`StagedPipelinesState`,
 * `AssignedResourcesState`/`StagedResourcesState`), not in the draft.
 */
export interface MethodTypeDraft {
  id?: string;
  name: string;
  assetTypeId: string;
  sampleNames: Record<string, string>[];
  /**
   * Hand-edited config.
   * When defined, takes the lead over sampleNames {@link sampleNames}
   */
  config?: Record<string, unknown>;
  acquisitionFields: MethodTypeAcquisitionField[];
}

/**
 * Payload of `seedFromDraft` — a staged method-type draft being re-opened,
 * plus the `persisted` flag the caller resolves (pending draft vs override).
 */
export interface MethodTypeDraftSeed {
  id: string;
  name: string;
  assetTypeId: string;
  assetTypeName?: string;
  sampleNames: Record<string, string>[];
  config?: Record<string, unknown>;
  acquisitionFields: MethodTypeAcquisitionField[];
  persisted: boolean;
}

/**
 * In-progress state of the manual JSON config editor. `value` is the latest
 * parsed config object (`null` while the JSON is malformed or not an object);
 * `valid` mirrors the JSON editor's parse state and gates the save. A non-null
 * `CustomConfigDraft` on the state means manual config mode is active — the
 * predefined-sample-names UI is replaced by the JSON editor (reversible via
 * `exitCustomConfig` while the draft stays conventional).
 */
export interface CustomConfigDraft {
  value: Record<string, unknown> | null;
  valid: boolean;
}

interface MethodTypeEditorStateModel {
  id: string | null;
  name: string;
  assetTypeId: string | null;
  assetTypeName: string | null;
  sampleNames: Record<string, string>[];
  customConfig: CustomConfigDraft | null;
  acquisitionFields: MethodTypeAcquisitionField[];
  /*
   * Whether the method type exists in the database. A create session carries
   * the page's placeholder id with `persisted: false`; the save cascade reads
   * the pair to pick create-vs-update (no id-prefix sniffing) and flips it via
   * `markPersisted` once the row save returns the real id — so a retry after a
   * later-phase failure takes the update path instead of duplicating the row.
   */
  persisted: boolean;
  /*
   * Facade-owned i18n keys for the save-validation errors (blank name /
   * missing asset type). Effects never write here — the facade's save gate
   * sets them and the corresponding updaters clear them on valid input.
   */
  nameError: string | null;
  assetTypeError: string | null;
  configError: string | null;
  loading: boolean;
  /*
   * Raw exception from the most recent `loadMethodType` failure. The facade
   * dispatches it to an i18n key in its own namespace.
   */
  loadException: Error | null;
  /** True while the cascade persists the method-type row (and its joins). */
  saving: boolean;
  /*
   * Raw exception from the most recent row/join save failure, recorded by the
   * save cascade (owner-state pattern, `PipelineEditorState.saveException`
   * parity). The facade dispatches it to an i18n key.
   */
  saveException: Error | null;
  /*
   * Signature snapshot captured when a seed completes (see
   * `MethodTypeEditorEffects.currentSignature$`). `null` until then, so a
   * session that never seeded (or whose load failed) reads as clean. Every
   * seed updater spreads INITIAL_STATE, clearing it per session;
   * `markPersisted` spreads the current state and preserves it.
   */
  baselineSignature: string | null;
}

const INITIAL_STATE: MethodTypeEditorStateModel = {
  id: null,
  name: '',
  assetTypeId: null,
  assetTypeName: null,
  sampleNames: [],
  customConfig: null,
  acquisitionFields: [],
  persisted: false,
  nameError: null,
  assetTypeError: null,
  configError: null,
  loading: false,
  loadException: null,
  saving: false,
  saveException: null,
  baselineSignature: null,
};

@Injectable()
export class MethodTypeEditorState extends ComponentStore<MethodTypeEditorStateModel> {
  /**
   * One-shot save/cancel outcome. Event, not state — never replays to late
   * subscribers, so a reopened editor can't consume a prior session's result.
   */
  private readonly _outcome$ = new Subject<MethodTypeEditorOutcome>();
  public readonly outcome$: Observable<MethodTypeEditorOutcome> = this._outcome$.asObservable();

  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly id$ = this.select((s) => s.id);
  public readonly name$ = this.select((s) => s.name);
  public readonly assetTypeId$ = this.select((s) => s.assetTypeId);
  public readonly assetTypeName$ = this.select((s) => s.assetTypeName);
  public readonly sampleNames$ = this.select((s) => s.sampleNames);
  public readonly customConfig$ = this.select((s) => s.customConfig);
  public readonly manualConfigMode$ = this.select((s) => s.customConfig !== null);
  public readonly customConfigValue$ = this.select((s) => s.customConfig?.value ?? null);
  public readonly customConfigValid$ = this.select((s) => s.customConfig?.valid ?? false);
  /**
   * Whether manual mode can be exited losslessly right now: the JSON draft
   * parses AND is conventional (`MethodTypeConfigService.isValidConfig`).
   * Deliberately the same rule that decides whether a saved config reopens in
   * the chips UI, so "can exit now" and "reopens structured after save" can
   * never drift apart.
   */
  public readonly canExitCustomConfig$ = this.select((s) => MethodTypeEditorState.isExitableDraft(s.customConfig));
  public readonly acquisitionFields$ = this.select((s) => s.acquisitionFields);
  public readonly persisted$ = this.select((s) => s.persisted);
  public readonly nameError$ = this.select((s) => s.nameError);
  public readonly assetTypeError$ = this.select((s) => s.assetTypeError);
  public readonly configError$ = this.select((s) => s.configError);
  public readonly loading$ = this.select((s) => s.loading);
  public readonly loadException$ = this.select((s) => s.loadException);
  public readonly saving$ = this.select((s) => s.saving);
  public readonly saveException$ = this.select((s) => s.saveException);
  public readonly baselineSignature$ = this.select((s) => s.baselineSignature);

  public readonly draft$ = this.select(
    this.id$,
    this.name$,
    this.assetTypeId$,
    this.sampleNames$,
    this.acquisitionFields$,
    this.customConfig$,
    (id, name, assetTypeId, sampleNames, acquisitionFields, customConfig): MethodTypeDraft => ({
      id: id ?? undefined,
      name,
      assetTypeId: assetTypeId ?? '',
      sampleNames,
      config: customConfig?.value ?? undefined,
      acquisitionFields,
    })
  );

  // ============================================================================
  // Updaters — metadata
  // ============================================================================

  /**
   * A non-blank name clears the pending validation error inline (the user
   * fixed it); a blank one leaves the error painted until the next save click
   * re-validates.
   */
  public readonly setName = this.updater((state, name: string) => ({
    ...state,
    name,
    nameError: name.trim() === '' ? state.nameError : null,
  }));

  /** Selecting any asset type satisfies the validation, so the error clears. */
  public readonly setAssetType = this.updater((state, assetType: { id: string; name: string }) => ({
    ...state,
    assetTypeId: assetType.id,
    assetTypeName: assetType.name,
    assetTypeError: null,
  }));

  // ============================================================================
  // Updaters — sample names
  // ============================================================================

  public readonly addSampleName = this.updater((state, values: Record<string, string>) => ({
    ...state,
    sampleNames: [...state.sampleNames, values],
  }));

  public readonly removeSampleName = this.updater((state, index: number) => ({
    ...state,
    sampleNames: state.sampleNames.filter((_, i) => i !== index),
  }));

  public readonly updateSampleName = this.updater(
    (state, payload: { index: number; values: Record<string, string> }) => ({
      ...state,
      sampleNames: state.sampleNames.map((values, i) => (i === payload.index ? payload.values : values)),
    })
  );

  // ============================================================================
  // Updaters — manual config
  // ============================================================================

  /**
   * Activate manual JSON config mode, seeding the editor with `value` (built
   * from the current sample names). A seeded value is a valid object, and any
   * stale config error is wiped — the user is starting fresh.
   */
  public readonly enterCustomConfig = this.updater((state, payload: { value: Record<string, unknown> }) => ({
    ...state,
    customConfig: { value: payload.value, valid: true },
    configError: null,
  }));

  /**
   * Record the JSON editor's latest parse state. A valid edit clears any
   * painted config error inline (the user fixed it), mirroring `setName`; an
   * invalid one leaves the error until the next save click re-validates.
   */
  public readonly setCustomConfig = this.updater(
    (state, payload: { value: Record<string, unknown> | null; valid: boolean }) => ({
      ...state,
      customConfig: { value: payload.value, valid: payload.valid },
      configError: payload.valid ? null : state.configError,
    })
  );

  /**
   * Exit manual mode by parsing the draft back into the chips: the JSON's
   * default samples become the sample names (the JSON wins — including
   * replacing them with nothing) and the draft is discarded. Self-guarding:
   * a no-op unless the draft is exitable — literally the same predicate
   * `canExitCustomConfig$` exposes to gate the UI.
   */
  public readonly exitCustomConfig = this.updater((state) => {
    if (!MethodTypeEditorState.isExitableDraft(state.customConfig)) return state;
    return {
      ...state,
      sampleNames: MethodTypeConfigService.toSampleNames(state.customConfig.value),
      customConfig: null,
    };
  });

  /** The single exit-enablement predicate — the draft parses and is conventional. */
  private static isExitableDraft(draft: CustomConfigDraft | null): draft is CustomConfigDraft {
    return draft !== null && draft.valid && MethodTypeConfigService.isValidConfig(draft.value);
  }

  // ============================================================================
  // Updaters — acquisition fields
  // ============================================================================

  public readonly addAcquisitionField = this.updater((state, field: MethodTypeAcquisitionField) => ({
    ...state,
    acquisitionFields: [...state.acquisitionFields, field],
  }));

  public readonly removeAcquisitionField = this.updater((state, index: number) => ({
    ...state,
    acquisitionFields: state.acquisitionFields.filter((_, i) => i !== index),
  }));

  // ============================================================================
  // Updaters — flags
  // ============================================================================

  public readonly setNameError = this.updater((state, nameError: string | null) => ({ ...state, nameError }));
  public readonly setAssetTypeError = this.updater((state, assetTypeError: string | null) => ({
    ...state,
    assetTypeError,
  }));
  public readonly setConfigError = this.updater((state, configError: string | null) => ({ ...state, configError }));
  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));
  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));
  // Plain slice setter (PipelineEditorState parity): the cascade flips `saving`
  // explicitly, so recording or clearing an exception never touches the phase.
  public readonly setSaveException = this.updater((state, saveException: Error | null) => ({
    ...state,
    saveException,
  }));

  // Setting an exception transitions out of the in-flight loading phase —
  // that's how "load failed" reaches the UI. Clearing (null), called
  // proactively at the start of a fresh attempt to wipe a stale error, must
  // NOT touch the phase flag: the surrounding effect has just flipped it to
  // true and we'd otherwise undo that.
  public readonly setLoadException = this.updater((state, loadException: Error | null) => ({
    ...state,
    loadException,
    loading: loadException === null ? state.loading : false,
  }));

  // Seed updaters spread `...INITIAL_STATE`, so the baseline auto-clears on
  // every re-seed — the capture effect must re-run per session.
  public readonly setBaselineSignature = this.updater((state, baselineSignature: string) => ({
    ...state,
    baselineSignature,
  }));

  // ============================================================================
  // Updaters — lifecycle
  // ============================================================================

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  /**
   * Seed a CREATE session: pristine editor carrying the page's placeholder id
   * (`tmp-<uuid>`) with `persisted: false`, so the save cascade knows both the
   * placeholder to remap and that the row does not exist yet.
   */
  public readonly seedForCreate = this.updater((_state, payload: { id: string }) => ({
    ...INITIAL_STATE,
    id: payload.id,
  }));

  /**
   * The row save returned the REAL id: swap the placeholder and mark the
   * method type persisted so any retry takes the update path.
   */
  public readonly markPersisted = this.updater((state, id: string) => ({
    ...state,
    id,
    persisted: true,
  }));

  /**
   * Seed the editor from a fresh load. Building on `INITIAL_STATE` also ends
   * any in-flight loading phase and wipes stale errors. The output's
   * `assignedResources` are deliberately NOT stored here — the load effect
   * seeds them into the shared `AssignedResourcesState` tree.
   */
  public readonly seedFromMethodType = this.updater((_state, output: RetrieveMethodTypeOutput) => ({
    ...INITIAL_STATE,
    id: output.id,
    name: output.name,
    assetTypeId: output.assetTypeId ?? null,
    assetTypeName: output.assetTypeName ?? null,
    sampleNames: [...output.sampleNames],
    // A free-form config (the query flags it) opens manual mode straight away,
    // seeded with the raw blob; a conventional config leaves it null.
    customConfig: output.customConfig ? { value: output.customConfig, valid: true } : null,
    acquisitionFields: [...output.acquisitionFields],
    persisted: true,
  }));

  /**
   * Seed the editor from a previously-staged draft (re-opening a method type
   * already edited this session, without a DB round-trip). `PipelineEditor`'s
   * `seedFromDraft` parity: the caller supplies the `persisted` flag so a re-edit
   * of a pending (`tmp-`) draft stays a create and a re-edit of an override stays
   * an update.
   */
  public readonly seedFromDraft = this.updater((_state, draft: MethodTypeDraftSeed) => ({
      ...INITIAL_STATE,
      id: draft.id,
      name: draft.name,
      assetTypeId: draft.assetTypeId || null,
      assetTypeName: draft.assetTypeName ?? null,
      sampleNames: [...draft.sampleNames],
      customConfig: draft.config ? { value: draft.config, valid: true } : null,
      acquisitionFields: [...draft.acquisitionFields],
      persisted: draft.persisted,
    })
  );

  // ============================================================================
  // Events
  // ============================================================================

  /** Emit the editing-session outcome as a one-shot event (see `outcome$`). */
  public confirm(outcome: MethodTypeEditorOutcome): void {
    this._outcome$.next(outcome);
  }

  override ngOnDestroy(): void {
    this._outcome$.complete();
    super.ngOnDestroy();
  }
}
